import { radioGroupKeys } from '../utils/radioGroupKeys.js';
import {cachedJSON} from '../utils/cachedJSON.js';
import {tonightPasses,passKey,skyTargets} from '../utils/tonightPasses.js';
import {tierColor as passTierColor} from '../utils/passTiers.js';
import {passWindow} from '../utils/observations.js';
import { usableVerdict, observingPresentation, observingWindow } from '../utils/observations.js';
import { useState, useEffect, useRef } from 'react';
import ShareVerdictCard from './ShareVerdictCard.jsx';
import DailyBriefing from './DailyBriefing.jsx';
import VerdictDrilldown from './VerdictDrilldown.jsx';
import { useKit } from '../hooks/useKit.js';
import { computeExposure, formatShutter, fLabel } from '../utils/exposure.js';
import { altAzAt, nextTransit, peakAltDeg, windowAbove } from '../utils/skymath.js';
import PopoutModal from './PopoutModal.jsx';
import { PASS_WINDOW_HOURS } from '../utils/passTiers.js';

const API = import.meta.env.VITE_API_BASE || '/api';

const MODES = [['sat','Satellites'], ['planet','Planets'], ['deepsky','Deep-sky']];
const ACTIVITIES = [
  ['iss','Satellites','sat'],
  ['deepsky','Deep-sky','deepsky'],
  ['lunar','Moon & planets','planet'],
  ['aurora','Aurora','sat'],
];
const TIER_LABEL = ['Below horizon', 'Naked-eye', 'Worth a look', 'Photograph'];
const TIER_ICON  = ['', 'ti-eye', 'ti-star', 'ti-camera'];
const EXPERIENCE = [['casual','Eyes'], ['scope','Scope'], ['beginner','Camera'], ['advanced','Pro']];
const VERDICT_COLOR = { Excellent:'var(--amber)', Good:'var(--green)', Marginal:'var(--cyan)', Poor:'var(--txt-mute)' };
const FACTOR_COLOR  = { good:'var(--green)', mid:'var(--cyan)', bad:'var(--rose)' };
// "Why this score" bars: vivid valence fills (the calm tokens read gray) + a
// bar length that encodes how favorable each factor is.
const VALENCE_COLOR = { good:'var(--green)', mid:'var(--cyan)', bad:'var(--rose)' };
const VALENCE_FILL  = { good:0.92, mid:0.55, bad:0.25 };

const COMPASS = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
const compass = d => d == null ? '' : COMPASS[Math.round(((d % 360) / 22.5)) % 16];
const fmtTime = iso => new Date(iso).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' });
function countdown(iso) {
  const m = Math.floor((new Date(iso) - Date.now()) / 60000);
  if (m < 0) return 'now';
  return m > 60 ? `in ${Math.floor(m/60)}h ${m%60}m` : `in ${m}m`;
}
function scoreColor(s) {
  if (s >= 70) return 'var(--amber)';
  if (s >= 45) return 'var(--green)';
  if (s >= 20) return 'var(--cyan)';
  return 'var(--txt-mute)';
}

function Briefing({ pass, experience, large = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let c = false;
    setLoading(true); setError(null); setData(null);
    fetch(`${API}/ai/pass-briefing`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pass, experience }),
    })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`briefing ${r.status}`)))
      .then(d => { if (!c) { setData(d); setLoading(false); } })
      .catch(e => { if (!c) { setError(e.message); setLoading(false); } });
    return () => { c = true; };
  }, [pass.norad_id, pass.start, experience]);
  if (loading) return <div style={{ padding:'8px 0', color:'var(--txt-dim)', fontSize: 'var(--ui-label)' }}>Writing briefing&hellip;</div>;
  if (error)   return <div style={{ padding:'8px 0', color:'var(--rose)', fontSize: 'var(--ui-label)' }}>Briefing unavailable.</div>;
  if (!data) return null;
  const Row = ({ icon, text }) => <BriefRow icon={icon} text={text} large={large} />;
  return (
    <div style={{ padding:'10px 0 4px' }}>
      {data.headline && <div style={{ fontFamily:'var(--serif)', fontStyle:'italic', fontSize: large ? 16 : 12.5, color:'var(--txt-bright)', lineHeight:1.4, marginBottom: large ? 10 : 8 }}>{data.headline}</div>}
      <Row icon="ti-compass" text={data.where_to_look} />
      <Row icon="ti-clock" text={data.timing} />
      <Row icon="ti-adjustments" text={data.settings} />
      <Row icon="ti-bulb" text={data.tip} />
    </div>
  );
}

export function VerdictBlock({ verdict, loading, activity, onActivity, observer, onFactorTap, compact = false }) {
  const v = verdict;
  const assessment = observingPresentation(v);
  const score = usableVerdict(v) ? v.score : null;
  const color = VERDICT_COLOR[v?.verdict] || 'var(--txt-mute)';
  const circ = 2 * Math.PI * 26;
  const off = circ * (1 - (score ?? 0) / 100);
  // Worst-factor callout (drill-down tier 0): surface what's hurting the score
  // FIRST, so the verdict explains itself before the user digs.
  const worst = v?.factors?.find(f => f.valence === 'bad') || null;

  // Compact variant (desktop left column): one header row + a one-line "why"
  // affordance. The factor bars live in VerdictDrilldown behind that tap, so
  // the column's vertical budget goes to the briefing and target list instead.
  if (compact) {
    const circC = 2 * Math.PI * 17;
    return (
      <div style={{ padding:'12px 18px 10px', borderBottom:'1px solid var(--hair)', flexShrink:0 }}>
        <div className="v2-segmented" data-fill="true" role="radiogroup" onKeyDown={radioGroupKeys} aria-label="Tonight activity" style={{ marginBottom:11 }}>
          {ACTIVITIES.map(([val, label]) => (
            <button key={val} role="radio" aria-checked={activity===val} tabIndex={activity===val ? 0 : -1} onClick={() => onActivity(val)}>{label}</button>
          ))}
        </div>
        <div className="og-verdict-summary" style={{ display:'flex', gap:12, alignItems:'center' }}>
          <div style={{ position:'relative', width:44, height:44, flexShrink:0 }}>
            <svg width="44" height="44" style={{ transform:'rotate(-90deg)' }}>
              <circle cx="22" cy="22" r="17" fill="none" stroke="var(--hair-2)" strokeWidth="4" />
              <circle cx="22" cy="22" r="17" fill="none" stroke={color} strokeWidth="4" strokeLinecap="round"
                strokeDasharray={circC} strokeDashoffset={circC * (1 - (score ?? 0) / 100)} style={{ transition:'stroke-dashoffset .5s, stroke .5s' }} />
            </svg>
            <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <span style={{ fontSize: 'var(--ui-body)', fontWeight:600, color:'var(--txt-bright)', fontFamily:'var(--mono)', lineHeight:1 }}>{loading ? '\u2026' : (score ?? '-')}</span>
            </div>
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontFamily:'var(--serif)', fontStyle:'italic', fontSize:17, color, lineHeight:1.05, marginBottom:2 }}>
              {loading ? 'Reading the sky\u2026' : assessment.headline}
            </div>
            <div style={{ fontFamily:'var(--mono)', fontSize: 'var(--ui-label)', color:'var(--txt-dim)', letterSpacing:'0.04em' }}>
              {ACTIVITIES.find(a=>a[0]===activity)?.[1] || v?.activity_label || 'observing'} · weighted conditions /100
            </div>
          </div>
          <ShareVerdictCard verdict={v} observer={observer} style={{ padding:'5px 9px', flexShrink:0 }} />
        </div>
        <div data-testid="observing-context" style={{fontSize: 'var(--ui-label)', color:'var(--txt-dim)', lineHeight:1.5, marginTop:8, overflowWrap:'anywhere'}}>
          {observer?.name || 'Observer'} · {observingWindow(v)}<br />{assessment.reason}<br />{assessment.light_pollution_note}
        </div>
        {onFactorTap && (
          <button onClick={() => onFactorTap(null)}
            style={{ display:'flex', alignItems:'center', gap:6, width:'100%', marginTop:9,
                     background:'transparent', border:'none', padding:'2px 0', textAlign:'left',
                     cursor:'pointer', fontFamily:'var(--mono)', fontSize: 'var(--ui-label)',
                     color: worst ? 'var(--warn)' : 'var(--txt-dim)' }}>
            {worst ? <>&#9650; {worst.key}: {worst.value}</> : <>{assessment.status === 'ready' ? 'Weighted conditions' : assessment.headline}</>}
            <span style={{ color:'var(--txt-mute)' }}>&middot; why this score &rsaquo;</span>
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding:'16px 18px', borderBottom:'1px solid var(--hair)', flexShrink:0 }}>
      <div className="v2-segmented" data-fill="true" role="radiogroup" onKeyDown={radioGroupKeys} aria-label="Tonight activity" style={{ marginBottom:14 }}>
        {ACTIVITIES.map(([val, label]) => (
          <button key={val} role="radio" aria-checked={activity===val} tabIndex={activity===val ? 0 : -1} onClick={() => onActivity(val)}>{label}</button>
        ))}
      </div>
      <div style={{ display:'flex', gap:16, alignItems:'center' }}>
        <div style={{ position:'relative', width:72, height:72, flexShrink:0 }}>
          <svg width="72" height="72" style={{ transform:'rotate(-90deg)' }}>
            <circle cx="36" cy="36" r="26" fill="none" stroke="var(--hair-2)" strokeWidth="5" />
            <circle cx="36" cy="36" r="26" fill="none" stroke={color} strokeWidth="5" strokeLinecap="round"
              strokeDasharray={circ} strokeDashoffset={off} style={{ transition:'stroke-dashoffset .5s, stroke .5s' }} />
          </svg>
          <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <span style={{ fontSize:21, fontWeight:500, color:'var(--txt-bright)', fontFamily:'var(--mono)', lineHeight:1 }}>{loading ? '\u2026' : (score ?? '-')}</span>
          </div>
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontFamily:'var(--serif)', fontStyle:'italic', fontSize:20, color, marginBottom:3, lineHeight:1.05 }}>
            {loading ? 'Reading the sky\u2026' : assessment.headline}
          </div>
          <div style={{ fontFamily:'var(--mono)', fontSize: 'var(--ui-label)', color:'var(--txt-dim)', letterSpacing:'0.04em' }}>
            {ACTIVITIES.find(a=>a[0]===activity)?.[1] || v?.activity_label || 'observing'} · weighted conditions /100
          </div>
        </div>
      </div>
      <div data-testid="observing-context" style={{fontSize: 'var(--ui-label)', color:'var(--txt-dim)', lineHeight:1.5, marginTop:10, overflowWrap:'anywhere'}}>
        {observer?.name || 'Observer'} · {observingWindow(v)}<br />{assessment.reason}<br />{assessment.light_pollution_note}
      </div>
      {worst && (
        <button
          onClick={onFactorTap ? () => onFactorTap(worst) : undefined}
          style={{
            display:'block', width:'100%', textAlign:'left', marginTop:12,
            padding:'8px 10px', background:'rgba(212,99,74,0.10)',
            border:'1px solid rgba(212,99,74,0.35)', borderRadius:4,
            color:'var(--warn)', fontFamily:'var(--mono)', fontSize: 'var(--ui-label)',
            cursor:onFactorTap ? 'pointer' : 'default',
          }}
        >
          &#9650; {worst.key}: {worst.value}{onFactorTap ? ' ›' : ''}
        </button>
      )}
      {v?.factors?.length > 0 && (
        <div style={{ marginTop:14 }}>
          <div style={{ fontFamily:'var(--mono)', fontSize: 'var(--ui-small)', letterSpacing:'0.14em', color:'var(--txt-mute)', marginBottom:9 }}>
            WHY THIS SCORE &middot; WEIGHTED{onFactorTap ? ' · TAP A FACTOR' : ''}
          </div>
          {v.factors.map((f, i) => {
            const c = VALENCE_COLOR[f.valence] || 'var(--txt-dim)';
            const fill = VALENCE_FILL[f.valence] ?? 0.5;
            const Row = onFactorTap ? 'button' : 'div';
            return (
              <Row key={i}
                onClick={onFactorTap ? () => onFactorTap(f) : undefined}
                style={{ display:'flex', alignItems:'center', gap:10, marginBottom:7, width:'100%',
                         background:'transparent', border:'none', padding:0, textAlign:'left',
                         cursor:onFactorTap ? 'pointer' : 'default',
                         ...(onFactorTap ? { minHeight:32 } : {}) }}>
                <span style={{ fontFamily:'var(--mono)', fontSize: 'var(--ui-label)', color:'var(--txt-dim)', width:92, flexShrink:0, letterSpacing:'0.02em', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{f.key}</span>
                <div style={{ flex:1, height:5, borderRadius:3, background:'var(--hair-2)', overflow:'hidden', minWidth:30 }}>
                  <div style={{ width:`${fill*100}%`, height:'100%', background:c, borderRadius:3, transition:'width .5s, background .5s' }} />
                </div>
                <span style={{ fontFamily:'var(--mono)', fontSize: 'var(--ui-label)', color:'var(--txt)', width:74, textAlign:'right', flexShrink:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{f.value}</span>
              </Row>
            );
          })}
        </div>
      )}
      <div style={{ marginTop:14, paddingTop:12, borderTop:'1px solid var(--hair)', display:'flex', justifyContent:'stretch' }}>
        <ShareVerdictCard verdict={v} observer={observer} style={{ flex:1, justifyContent:'center' }} />
      </div>
    </div>
  );
}

function SatRow({ p, expanded, setExpanded, experience, setExperience, onGoto, onPopout, onFocus, focusedKey }) {
  const tier = p.visibility_tier || 0, color = passTierColor(tier), key = `${p.norad_id}_${p.start}`;
  const isOpen = expanded === key;
  return (
    <div data-pass-key={passKey(p)} style={{ borderBottom:'1px solid var(--hair)', borderLeft:`2px solid ${color}` }}>
      <div style={{ padding:'11px 18px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5 }}>
          <span style={{ fontSize: 'var(--ui-body)', color:'var(--txt-bright)', fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:200 }}>{p.name}</span>
          <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize: 'var(--ui-small)', letterSpacing:'0.08em', padding:'2px 7px', borderRadius:99, background:'var(--bg-2)', color, border:`1px solid ${color}` }}>
            <i className={`ti ${TIER_ICON[tier]}`} style={{ fontSize: 'var(--ui-label)' }} />{TIER_LABEL[tier]}
          </span>
        </div>
        <div style={{ display:'flex', gap:14, fontSize: 'var(--ui-label)', color:'var(--txt-dim)', flexWrap:'wrap', marginBottom:8 }}>
          <span><span style={{ color:'var(--txt-bright)' }}>{fmtTime(p.start)}</span> {countdown(p.start)}</span>
          <span>max <span style={{ color:'var(--txt)' }}>{p.max_el?.toFixed(0)}&deg;</span> {compass(p.azimuth_max)}</span>
          <span>{Math.round((p.duration_sec||0)/60)}m{(p.duration_sec||0)%60}s</span>
          {p.approx_magnitude!=null && <span>mag <span style={{ color:'var(--txt)' }}>{p.approx_magnitude>0?'+':''}{p.approx_magnitude}</span></span>}
        </div>
        {p.best_observing_window && <div style={{ fontSize: 'var(--ui-label)', color:'var(--txt-dim)', marginBottom:8 }}>
          Sunlit observing window: {fmtTime(p.best_observing_window.start)}–{fmtTime(p.best_observing_window.end)}
          {' · '}{Math.round(p.best_observing_window.duration_sec)}s · peak {p.best_observing_window.max_el?.toFixed(0)}°
        </div>}
        <div className="og-pass-actions" style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          {onFocus && <button className="v2-tonight-btn" aria-pressed={focusedKey===passKey(p)} onClick={()=>onFocus(p)}>Show on sky</button>}
          <button className="v2-tonight-btn v2-tonight-btn--briefing" aria-pressed={isOpen}
                  onClick={() => setExpanded(isOpen ? null : key)}>
            <i className="ti ti-sparkles" aria-hidden="true" style={{ fontSize: 'var(--ui-label)' }} />{isOpen ? 'Hide briefing' : 'Briefing'}
          </button>
          <button className="v2-tonight-btn v2-tonight-btn--ghost"
                  onClick={() => onGoto?.({ norad_1: p.norad_id, title: p.name })} title="Open in Explore">
            <i className="ti ti-crosshair" style={{ fontSize: 'var(--ui-label)' }} />Explore
          </button>
          <button className="v2-tonight-btn v2-tonight-btn--ghost" title="Pop out briefing"
                  aria-label={`Pop out ${p.name} briefing`} onClick={() => onPopout?.(p)}>
            <i className="ti ti-arrows-diagonal" style={{ fontSize: 'var(--ui-label)' }} />
          </button>
        </div>
        {isOpen && (
          <>
            {/* Detail-level selector lives with the content it controls. */}
            <div className="og-briefing-level" style={{ padding:'8px 0 0' }}>
              <span style={{ fontFamily:'var(--mono)', fontSize: 'var(--ui-small)', letterSpacing:'0.06em', color:'var(--txt-mute)' }}>BRIEFING FOR</span>
              <div className="v2-segmented og-briefing-tier" data-variant="pill" role="radiogroup" onKeyDown={radioGroupKeys} aria-label="Briefing detail level">
                {EXPERIENCE.map(([lv,l]) => (
                  <button key={lv} role="radio" aria-checked={experience===lv} tabIndex={experience===lv ? 0 : -1} onClick={() => setExperience(lv)}>{l}</button>
                ))}
              </div>
            </div>
            <Briefing pass={p} experience={experience} />
          </>
        )}
      </div>
    </div>
  );
}

// Shared fact/prose row used by both briefing renderers and the deterministic
// fixed-target facts.
function BriefRow({ icon, text, warn = false, large = false }) {
  if (!text) return null;
  return (
    <div style={{ display:'flex', gap:8, marginBottom: large ? 9 : 6, alignItems:'flex-start' }}>
      <i className={`ti ${icon}`} style={{ fontSize: large ? 15 : 12, color: warn ? 'var(--warn)' : 'var(--accent)', marginTop:1, flexShrink:0 }} />
      <span style={{ fontSize: large ? 13.5 : 11, lineHeight: large ? 1.6 : 1.5, color: warn ? 'var(--warn)' : 'var(--txt)' }}>{text}</span>
    </div>
  );
}

// AI briefing for a FIXED target (planet / DSO), Option-2 layer on top of the
// deterministic facts. Mirrors the satellite Briefing: tiered by experience,
// server-cached 30min, same JSON contract.
function FixedBriefing({ target, experience, large = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let c = false;
    setLoading(true); setError(null); setData(null);
    fetch(`${API}/ai/target-briefing`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ target, experience }),
    })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`briefing ${r.status}`)))
      .then(d => { if (!c) { setData(d); setLoading(false); } })
      .catch(e => { if (!c) { setError(e.message); setLoading(false); } });
    return () => { c = true; };
  }, [target.name, target.kind, experience]); // eslint-disable-line react-hooks/exhaustive-deps
  if (loading) return <div style={{ padding:'8px 0', color:'var(--txt-dim)', fontSize: 'var(--ui-label)' }}>Writing briefing&hellip;</div>;
  if (error)   return <div style={{ padding:'8px 0', color:'var(--rose)', fontSize: 'var(--ui-label)' }}>Briefing unavailable.</div>;
  if (!data) return null;
  return (
    <div style={{ padding:'10px 0 4px' }}>
      {data.headline && <div style={{ fontFamily:'var(--serif)', fontStyle:'italic', fontSize: large ? 16 : 12.5, color:'var(--txt-bright)', lineHeight:1.4, marginBottom: large ? 10 : 8 }}>{data.headline}</div>}
      <BriefRow large={large} icon="ti-compass" text={data.where_to_look} />
      <BriefRow large={large} icon="ti-clock" text={data.timing} />
      <BriefRow large={large} icon="ti-adjustments" text={data.settings} />
      <BriefRow large={large} icon="ti-bulb" text={data.tip} />
    </div>
  );
}

function StaticRow({ o, isPlanet, observer, verdict, kit, experience, setExperience, expanded, setExpanded, onPopout }) {
  const score = isPlanet ? null : o.score;
  const color = isPlanet ? 'var(--cyan)' : scoreColor(score);
  const alt = isPlanet ? o.elevation_deg : o.altitude_deg;
  const az  = o.azimuth_deg;
  const label = isPlanet ? o.name : (o.name || o.id);
  const sub   = isPlanet ? (o.type==='moon'?'Moon':'planet') : `${o.id} · ${o.type}`;
  const rowKey = `fixed_${o.id || label}`;
  const isOpen = expanded === rowKey;
  const [showAI, setShowAI] = useState(false);

  // WHEN/WHERE, pure sidereal math from the ra/dec the APIs already return.
  // No network, no AI: transit time, peak altitude, bearing at peak, and the
  // above-30° imaging window.
  const now = Date.now();
  let when = null;
  if (o.ra_hours != null && o.dec_deg != null && observer?.lat != null) {
    const transit = nextTransit(o.ra_hours, observer.lon, now);
    const peak    = peakAltDeg(o.dec_deg, observer.lat);
    const azPeak  = altAzAt(o.ra_hours, o.dec_deg, observer.lat, observer.lon, transit).az;
    const win     = windowAbove(o.ra_hours, o.dec_deg, observer.lat, observer.lon, 30, transit);
    when = { transit, peak, azPeak, win };
  }
  const kind = isPlanet ? (o.type==='moon'?'moon':'planet') : (o.type || 'deepsky');
  const recipe = isOpen ? computeExposure({ kit, target: { kind, label }, conditions: verdict?.conditions }) : null;
  const moonClose = !isPlanet && o.moon_sep_deg != null && o.moon_sep_deg < 30;
  const winText = when == null ? null
    : when.win.kind === 'window' ? `above 30° ${fmtTime(when.win.t0)}–${fmtTime(when.win.t1)}`
    : when.win.kind === 'always' ? 'above 30° all night'
    : 'stays below 30°';
  const aiTarget = isOpen ? {
    name: label, kind, type: o.type, con: o.con,
    mag: o.mag, size_arcmin: o.size_arcmin,
    alt_now: alt, az_now: az,
    transit_iso: when ? new Date(when.transit).toISOString() : null,
    peak_alt: when ? Math.round(when.peak) : null,
    az_peak: when ? Math.round(when.azPeak) : null,
    window: winText,
    moon_sep_deg: o.moon_sep_deg,
    darkness: verdict?.conditions?.darkness,
    bortle: verdict?.conditions?.bortle,
  } : null;
  return (
    <div style={{ borderBottom:'1px solid var(--hair)', borderLeft:`2px solid ${color}` }}>
      <button onClick={() => { setExpanded(isOpen ? null : rowKey); if (isOpen) setShowAI(false); }}
              aria-expanded={isOpen}
              style={{ display:'block', width:'100%', textAlign:'left', background:'transparent', border:'none', padding:'11px 18px 0', cursor:'pointer', fontFamily:'inherit', color:'inherit' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
          <span style={{ fontSize: 'var(--ui-body)', color:'var(--txt-bright)', fontWeight:500 }}>{label}</span>
          <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
            {!isPlanet && <span style={{ fontSize: 'var(--ui-small)', letterSpacing:'0.08em', padding:'2px 7px', borderRadius:99, background:'var(--bg-2)', color, border:`1px solid ${color}` }}>score {score}</span>}
            <i className={`ti ti-chevron-${isOpen ? 'up' : 'down'}`} style={{ fontSize: 'var(--ui-label)', color:'var(--txt-mute)' }} />
          </span>
        </div>
        <div style={{ fontSize: 'var(--ui-label)', color:'var(--txt-mute)', marginBottom:6 }}>{sub}{o.con ? ` · ${o.con}` : ''}</div>
        <div style={{ display:'flex', gap:14, fontSize: 'var(--ui-label)', color:'var(--txt-dim)', flexWrap:'wrap', paddingBottom: isOpen ? 0 : 11 }}>
          <span>alt <span style={{ color:'var(--txt)' }}>{alt?.toFixed(0)}&deg;</span> {compass(az)}</span>
          {o.mag!=null && <span>mag <span style={{ color:'var(--txt)' }}>{o.mag>0?'+':''}{o.mag}</span></span>}
          {o.size_arcmin && <span>{o.size_arcmin}&#39;</span>}
          {o.moon_sep_deg!=null && <span>moon {o.moon_sep_deg}&deg;</span>}
        </div>
      </button>
      {isOpen && (
        <div style={{ padding:'8px 18px 11px' }}>
          {when && <BriefRow icon="ti-clock" text={`Highest ${fmtTime(when.transit)} at ${Math.round(when.peak)}° · ${winText}`} />}
          {when && <BriefRow icon="ti-compass" text={`${compass(az)} ${alt?.toFixed(0)}° now → ${compass(when.azPeak)} at its highest`} />}
          {moonClose && <BriefRow icon="ti-moon" warn text={`Moon only ${o.moon_sep_deg}° away, expect washout on faint detail`} />}
          {recipe && (recipe.iso != null
            ? <BriefRow icon="ti-adjustments" text={`${recipe.target_label}: ISO ${recipe.iso} · ${fLabel(recipe.aperture)} · ${formatShutter(recipe.shutter)}${recipe.subs > 1 ? ` × ${recipe.subs}` : ''}${recipe.total_minutes ? ` ≈ ${recipe.total_minutes}m` : ''}${recipe.tracking ? ' · tracker' : ''}`} />
            : <BriefRow icon="ti-adjustments" text={recipe.why || 'Visual target, eyes or binoculars tonight.'} />)}
          <div className="og-briefing-level" style={{ padding:'6px 0 0' }}>
            <button className="v2-tonight-btn v2-tonight-btn--briefing" aria-pressed={showAI}
                    onClick={() => setShowAI(s => !s)}>
              <i className="ti ti-sparkles" aria-hidden="true" style={{ fontSize: 'var(--ui-label)' }} />{showAI ? 'Hide briefing' : 'Briefing'}
            </button>
            <button className="v2-tonight-btn v2-tonight-btn--ghost" title="Pop out"
                    aria-label={`Pop out ${label} details`}
                    onClick={() => onPopout?.({ kind:'fixed', o, isPlanet, label, when, winText, tkind: kind, aiTarget })}>
              <i className="ti ti-arrows-diagonal" style={{ fontSize: 'var(--ui-label)' }} />
            </button>
            {showAI && (
              <div className="v2-segmented og-briefing-tier" data-variant="pill" role="radiogroup" onKeyDown={radioGroupKeys} aria-label="Briefing detail level">
                {EXPERIENCE.map(([lv,l]) => (
                  <button key={lv} role="radio" aria-checked={experience===lv} tabIndex={experience===lv ? 0 : -1} onClick={() => setExperience(lv)}>{l}</button>
                ))}
              </div>
            )}
          </div>
          {showAI && aiTarget && <FixedBriefing target={aiTarget} experience={experience} />}
        </div>
      )}
    </div>
  );
}

const CENTER_VIEWS = [
  ['sky',  '🔭 SKY',   '✦ CHART', 'All-sky pass plot / sky chart'],
  ['map',  '🗺 MAP',  null,        'Light-pollution map'],
  ['gear', '⚙ GEAR',  null,        'Gear & settings guide'],
];

export default function TonightBriefing({ observer, verdict, loading, activity, onActivity, onGoto, mobile = false, hideVerdict = false, centerView = null, onCenterView = null, targetMode=null, passes:sharedPasses, passLoading=false, passError=null, onFocus, focusedKey }) {
  const rs = mobile ? { ...rootStyle, height:'auto', borderRight:'none', overflow:'visible' } : rootStyle;
  const listStyle = mobile
    ? { overflowY:'visible', fontFamily:'var(--mono)', color:'var(--txt)' }
    : { overflowY:'auto', flex:1, fontFamily:'var(--mono)', color:'var(--txt)' };
  const [ownMode, setMode] = useState('sat');
  const mode=targetMode || ownMode;
  const [items, setItems] = useState([]);
  const [conditions, setConditions] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState(null);
  const [experience, setExperience] = useState('beginner');
  const [expanded, setExpanded] = useState(null);
  const [drillOpen, setDrillOpen] = useState(false);
  const [popout, setPopout] = useState(null); // { kind:'sat', p } | { kind:'fixed', ... }
  const { kit } = useKit();

  // Kit-aware briefing register: a saved scope defaults the tier to 'scope'
  // (visual, at the eyepiece) until the user explicitly picks another.
  const userChoseTier = useRef(false);
  const chooseExperience = (v) => { userChoseTier.current = true; setExperience(v); };
  useEffect(() => {
    if (kit?.scope && String(kit.scope).trim() && !userChoseTier.current) setExperience('scope');
  }, [kit?.scope]);

  // Keep the target list in sync with the chosen activity (ISS/Aurora → sats,
  // Lunar → planets, Deep-sky → deep-sky) while still allowing manual override.
  useEffect(() => {
    const m = ACTIVITIES.find(x => x[0] === activity)?.[2];
    if (m) setMode(m);
  }, [activity]);

  useEffect(()=>setExpanded(null),[mode,observer?.lat,observer?.lon]);
  useEffect(() => {
    setItems([]);setConditions(null);setError(null);
    if (!observer) {setListLoading(false);return;}
    if (mode==='sat' && sharedPasses) {setItems(sharedPasses);setListLoading(passLoading);setError(passError);return;}
    let cancelled=false;setListLoading(true);
    const {lat,lon}=observer;
    const url=mode==='sat'?`${API}/passes/tonight?lat=${lat}&lon=${lon}&hours=${PASS_WINDOW_HOURS}`:mode==='planet'?`${API}/sky?lat=${lat}&lon=${lon}`:`${API}/deepsky?lat=${lat}&lon=${lon}`;
    cachedJSON(url).then(d=>{
      if(cancelled)return;
      if(mode==='sat')setItems(tonightPasses(d.passes));
      else if(mode==='planet') {setItems(skyTargets(d,mode));setConditions(d.bodies?.find(b=>b.type==='moon'));}
      else {setItems(skyTargets(d,mode));setConditions(d.conditions);}
      setListLoading(false);
    }).catch(()=>{if(!cancelled){setError('Targets are unavailable. Try reopening the briefing shortly.');setListLoading(false);}});
    return()=>{cancelled=true;};
  }, [mode,observer?.lat,observer?.lon,sharedPasses,passLoading,passError]);

  if (!observer) {
    return (
      <div style={rs}>
        <div style={{ padding:'40px 22px', color:'var(--txt-dim)', fontSize: 'var(--ui-body)', textAlign:'center', lineHeight:1.6 }}>
          <i className="ti ti-map-pin-off" style={{ fontSize:24, display:'block', marginBottom:10, color:'var(--txt-mute)' }} />
          Set your location to see what&#39;s worth watching tonight.
        </div>
      </div>
    );
  }

  return (
    <div style={rs}>
      {!hideVerdict && <VerdictBlock verdict={verdict} loading={loading} activity={activity} onActivity={onActivity} observer={observer}
                                     compact={!mobile} onFactorTap={!mobile ? () => setDrillOpen(true) : undefined} />}

      {/* Hero briefing, the night's thesis, first (cached /api/briefing; no per-view AI cost). */}
      {!mobile && !hideVerdict && <DailyBriefing observer={observer} />}

      {centerView && onCenterView && !mobile && (
        <div style={{ padding:'10px 18px 0', flexShrink:0 }}>
          <div className="v2-segmented" data-fill="true" role="radiogroup" onKeyDown={radioGroupKeys} aria-label="Center view">
            {CENTER_VIEWS.map(([val, label, altLabel, title]) => {
              const isChart = val === 'sky' && (mode === 'planet' || mode === 'deepsky');
              return (
                <button key={val} role="radio" aria-checked={centerView===val} tabIndex={centerView===val ? 0 : -1} title={title}
                        onClick={() => onCenterView(val)}>
                  {isChart && altLabel ? altLabel : label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {!targetMode && <div className="v2-segmented" data-fill="true" role="radiogroup" onKeyDown={radioGroupKeys} aria-label="Target type"
           style={{ padding:'10px 18px 0', flexShrink:0 }}>
        {MODES.map(([val, label]) => (
          <button key={val} role="radio" aria-checked={mode===val} tabIndex={mode===val ? 0 : -1} onClick={() => setMode(val)}>{label}</button>
        ))}
      </div>}

      {conditions && mode === 'deepsky' && (
        <div style={{ padding:'7px 18px', borderBottom:'1px solid var(--hair)', fontFamily:'var(--mono)', fontSize: 'var(--ui-label)', letterSpacing:'0.04em', color:'var(--txt-mute)' }}>
          {conditions.darkness} sky &middot; moon {Math.round((conditions.moon_phase_fraction||0)*100)}%{conditions.moon_up ? ` up ${conditions.moon_alt_deg?.toFixed(0)}\u00b0` : ' down'}
        </div>
      )}

      {targetMode && <h3 className="og-target-heading">{mode==='sat'?'Visible passes · soonest first':'Sky chart targets · up to 8'}</h3>}
      <div style={listStyle}>
        {listLoading && <div style={{ padding:'28px 18px', color:'var(--txt-dim)', fontSize: 'var(--ui-label)', textAlign:'center' }}>Computing&hellip;</div>}
        {error && <div style={{ padding:'18px', color:'var(--rose)', fontSize: 'var(--ui-label)' }}>{error}</div>}
        {!listLoading && !error && items.length === 0 && (
          <div style={{ padding:'28px 18px', color:'var(--txt-dim)', fontSize: 'var(--ui-label)', textAlign:'center', lineHeight:1.6 }}>
            {mode === 'sat' ? `No visible passes among selected catalog candidates in the next ${PASS_WINDOW_HOURS} hours.` : mode === 'planet' ? 'No Moon or planets above 5° now.' : 'No deep-sky targets above 10° now.'}
          </div>
        )}
        {!listLoading && items.map((it, i) =>
          mode === 'sat'    ? <SatRow key={passKey(it)} p={it} onFocus={onFocus} focusedKey={focusedKey} expanded={expanded} setExpanded={setExpanded} experience={experience} setExperience={chooseExperience} onGoto={onGoto} onPopout={(p) => setPopout({ kind:'sat', p })} /> :
          mode === 'planet' ? <StaticRow key={i} o={it} isPlanet observer={observer} verdict={verdict} kit={kit} experience={experience} setExperience={chooseExperience} expanded={expanded} setExpanded={setExpanded}  onPopout={setPopout} /> :
                              <StaticRow key={i} o={it} isPlanet={false} observer={observer} verdict={verdict} kit={kit} experience={experience} setExperience={chooseExperience} expanded={expanded} setExpanded={setExpanded}  onPopout={setPopout} />
        )}
      </div>

      {drillOpen && <VerdictDrilldown verdict={verdict} onClose={() => setDrillOpen(false)} />}

      {/* Pop-out reading views, full-size type for small screens. */}
      {popout?.kind === 'sat' && (
        <PopoutModal title={popout.p.name} onClose={() => setPopout(null)}>
          <div style={{ fontFamily:'var(--mono)', fontSize: 'var(--ui-body)', color:'var(--txt-dim)', padding:'12px 0 4px', display:'flex', gap:16, flexWrap:'wrap' }}>
            <span><span style={{ color:'var(--txt-bright)' }}>{passWindow(popout.p)}</span> {countdown(popout.p.start)}</span>
            <span>max <span style={{ color:'var(--txt)' }}>{popout.p.max_el?.toFixed(0)}&deg;</span> {compass(popout.p.azimuth_max)}</span>
            <span>{Math.round((popout.p.duration_sec||0)/60)}m{(popout.p.duration_sec||0)%60}s</span>
            {popout.p.approx_magnitude!=null && <span>mag {popout.p.approx_magnitude>0?'+':''}{popout.p.approx_magnitude}</span>}
          </div>
          <div className="og-briefing-level" style={{ padding:'4px 0 6px' }}>
            <span style={{ fontFamily:'var(--mono)', fontSize: 'var(--ui-label)', letterSpacing:'0.06em', color:'var(--txt-mute)' }}>BRIEFING FOR</span>
            <div className="v2-segmented og-briefing-tier" data-variant="pill" role="radiogroup" onKeyDown={radioGroupKeys} aria-label="Briefing detail level">
              {EXPERIENCE.map(([lv,l]) => (
                <button key={lv} role="radio" aria-checked={experience===lv} tabIndex={experience===lv ? 0 : -1} onClick={() => chooseExperience(lv)}>{l}</button>
              ))}
            </div>
          </div>
          <Briefing pass={popout.p} experience={experience} large />
        </PopoutModal>
      )}
      {popout?.kind === 'fixed' && (
        <PopoutModal title={popout.label} onClose={() => setPopout(null)}>
          <div style={{ padding:'12px 0 0' }}>
            {popout.when && <BriefRow large icon="ti-clock" text={`Highest ${fmtTime(popout.when.transit)} at ${Math.round(popout.when.peak)}° · ${popout.winText}`} />}
            {popout.when && <BriefRow large icon="ti-compass" text={`${compass(popout.o.azimuth_deg)} ${(popout.isPlanet ? popout.o.elevation_deg : popout.o.altitude_deg)?.toFixed(0)}° now → ${compass(popout.when.azPeak)} at its highest`} />}
            {!popout.isPlanet && popout.o.moon_sep_deg != null && popout.o.moon_sep_deg < 30 &&
              <BriefRow large warn icon="ti-moon" text={`Moon only ${popout.o.moon_sep_deg}° away, expect washout on faint detail`} />}
            {(() => { const r = computeExposure({ kit, target:{ kind: popout.tkind, label: popout.label }, conditions: verdict?.conditions });
              return r.iso != null
                ? <BriefRow large icon="ti-adjustments" text={`${r.target_label}: ISO ${r.iso} · ${fLabel(r.aperture)} · ${formatShutter(r.shutter)}${r.subs>1?` × ${r.subs}`:''}${r.total_minutes?` ≈ ${r.total_minutes}m`:''}${r.tracking?' · tracker':''}`} />
                : <BriefRow large icon="ti-adjustments" text={r.why || 'Visual target, eyes or binoculars tonight.'} />; })()}
          </div>
          <div className="og-briefing-level" style={{ padding:'6px 0' }}>
            <span style={{ fontFamily:'var(--mono)', fontSize: 'var(--ui-label)', letterSpacing:'0.06em', color:'var(--txt-mute)' }}>BRIEFING FOR</span>
            <div className="v2-segmented og-briefing-tier" data-variant="pill" role="radiogroup" onKeyDown={radioGroupKeys} aria-label="Briefing detail level">
              {EXPERIENCE.map(([lv,l]) => (
                <button key={lv} role="radio" aria-checked={experience===lv} tabIndex={experience===lv ? 0 : -1} onClick={() => chooseExperience(lv)}>{l}</button>
              ))}
            </div>
          </div>
          {popout.aiTarget && <FixedBriefing target={popout.aiTarget} experience={experience} large />}
        </PopoutModal>
      )}
    </div>
  );
}

const rootStyle = {
  display:'flex', flexDirection:'column', height:'100%',
  borderRight:'1px solid var(--hair)', background:'var(--bg)', overflow:'hidden',
};
